import PropTypes from 'prop-types'
import React from 'react'
import { compose } from 'recompose'
import { connect as connectRedux } from 'react-redux'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { get as getConfig } from '../../config/index'
import { parse as parseQuery, stringify as stringifyQuery } from '../../services/query-string/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'

const config = getConfig()
const optimizedImagesEnabled = !!_get(config, ['features', 'optimizedImages'])

export const HERO_IMAGE_OVERLAY_OPACITY_SUPER_LIGHT = 'overlaySuperLight'
export const HERO_IMAGE_OVERLAY_OPACITY_LIGHT = 'overlayLight'
export const HERO_IMAGE_OVERLAY_OPACITY_MEDIUM = 'overlayMedium'
export const HERO_IMAGE_OVERLAY_OPACITY_DARK = 'overlayDark'
export const HERO_IMAGE_OVERLAY_LEFT_TO_RIGHY_OPACITY = 'leftToRight'
export const HERO_IMAGE_OVERLAY_TO_RIGHY_OPACITY_DARK = 'toRightDark'

function getClassName (className) {
  return ['hero-image'].concat(className || []).join(' ')
}

class HeroImage extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      loaded: false,
    }
  }

  componentDidMount () {
    const { props } = this
    const { optimizeImage } = props

    if (optimizedImagesEnabled && optimizeImage) {
      const imageSrc = _get(this.getOptimizedImages(), 'preloadImageUrl')

      if (imageSrc) {
        this.Image = new Image()
        this.Image.src = imageSrc
        this.Image.onload = () => {
          this.setState(() => ({ loaded: true }))
          this.destroyImage()
        }
      }
    } else {
      const imageSrc = this.getTrueImage()

      if (imageSrc) {
        this.Image = new Image()
        this.Image.src = this.getTrueImage()
        this.Image.onload = () => {
          this.setState(() => ({ loaded: true }))
          this.destroyImage()
        }
      }
    }
  }

  componentWillUnmount () {
    this.destroyImage()
  }

  getTrueImage () {
    const { props } = this
    const { alwaysUseProps } = props
    if (alwaysUseProps || !this.url) {
      const {
        url, largeUrl, mediumUrl, smallUrl,
      } = props
      this.url = url || largeUrl || mediumUrl || smallUrl
    }
    return this.url
  }

  getOptimizedImages () {
    const { props } = this
    const { url, viewportWidth } = props
    let smallImageUrl = ''
    let mediumSmallImageUrl = ''
    let mediumImageUrl = ''
    let largeImageUrl = ''
    let xLargeImageUrl = ''
    let fallbackImageUrl = ''
    let preloadImageUrl = ''

    try {
      const fallbackImageUrlObject = new URL(url)
      const imageUrlObject = new URL(url)
      const smallImageUrlObject = new URL(url)
      const mediumSmallImageUrlObject = new URL(url)
      const mediumImageUrlObject = new URL(url)
      const largeImageUrlObject = new URL(url)
      const xLargeImageUrlObject = new URL(url)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: '1440',
        fm: 'jpg',
        compress: 'auto',
      }
      const smallImageOptions = {
        ...parsedImageQuery,
        w: '800',
        fm: 'webp',
        q: '100',
      }
      const mediumSmallImageOptions = {
        ...parsedImageQuery,
        w: '1000',
        fm: 'webp',
        q: '100',
      }
      const mediumImageOptions = {
        ...parsedImageQuery,
        w: '1200',
        fm: 'webp',
        q: '95',
      }
      const largeImageOptions = {
        ...parsedImageQuery,
        w: '1440',
        fm: 'webp',
        q: '90',
      }
      const xLargeImageOptions = {
        ...parsedImageQuery,
        w: '1920',
        fm: 'webp',
        q: '85',
      }

      const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
      const stringifiedSmallImageQuery = stringifyQuery(smallImageOptions)
      const stringifiedMediumSmallImageQuery = stringifyQuery(mediumSmallImageOptions)
      const stringifiedMediumImageQuery = stringifyQuery(mediumImageOptions)
      const stringifiedLargeImageQuery = stringifyQuery(largeImageOptions)
      const stringifiedXLargeImageQuery = stringifyQuery(xLargeImageOptions)

      // set the search string back onto the fallback URL
      _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

      // set the search string back onto the image URLs
      _set(smallImageUrlObject, 'search', stringifiedSmallImageQuery)
      _set(mediumSmallImageUrlObject, 'search', stringifiedMediumSmallImageQuery)
      _set(mediumImageUrlObject, 'search', stringifiedMediumImageQuery)
      _set(largeImageUrlObject, 'search', stringifiedLargeImageQuery)
      _set(xLargeImageUrlObject, 'search', stringifiedXLargeImageQuery)

      fallbackImageUrl = fallbackImageUrlObject.toString()
      smallImageUrl = smallImageUrlObject.toString()
      mediumSmallImageUrl = mediumSmallImageUrlObject.toString()
      mediumImageUrl = mediumImageUrlObject.toString()
      largeImageUrl = largeImageUrlObject.toString()
      xLargeImageUrl = xLargeImageUrlObject.toString()

      // determine the proper image to preload
      if (viewportWidth <= 480) {
        preloadImageUrl = smallImageUrl
      }

      if (viewportWidth > 480 && viewportWidth <= 768) {
        preloadImageUrl = mediumSmallImageUrl
      }

      if (viewportWidth > 768 && viewportWidth <= 944) {
        preloadImageUrl = mediumImageUrl
      }

      if (viewportWidth > 944 && viewportWidth <= 1200) {
        preloadImageUrl = largeImageUrl
      }

      if (viewportWidth > 1200) {
        preloadImageUrl = xLargeImageUrl
      }
    } catch (e) {
      fallbackImageUrl = url
      smallImageUrl = url
      mediumSmallImageUrl = url
      mediumImageUrl = url
      largeImageUrl = url
      xLargeImageUrl = url
      preloadImageUrl = url
    }

    return {
      fallbackImageUrl,
      smallImageUrl,
      mediumSmallImageUrl,
      mediumImageUrl,
      largeImageUrl,
      xLargeImageUrl,
      preloadImageUrl,
    }
  }

  destroyImage () {
    if (this.Image) {
      this.Image.onload = null
      this.Image = null
    }
  }

  renderImage () {
    const { state } = this
    const { loaded } = state
    const imageUrl = this.getTrueImage()

    return (
      <div className={`hero-image__hero${loaded ? ' loaded' : ''}`}>
        {
          imageUrl
            ? <img src={imageUrl} className="hero-image__image" />
            : <div className="hero-image__image" />
        }
      </div>
    )
  }

  renderOptimizedImage () {
    const { state } = this
    const { loaded } = state
    const imageUrls = this.getOptimizedImages() || {}
    const {
      fallbackImageUrl,
      smallImageUrl,
      mediumSmallImageUrl,
      mediumImageUrl,
      largeImageUrl,
      xLargeImageUrl,
    } = imageUrls

    return (
      <div className={`hero-image__hero${loaded ? ' loaded' : ''}`}>
        {
          smallImageUrl
            ? <Picture
                fallbackSrc={fallbackImageUrl}
                width={'1440'}
                height={'300'}
                alt=""
                imageClassName="hero-image__image"
                loading={IMAGE_LOADING_LAZY}
                sources={[
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: smallImageUrl,
                    media: '(max-width: 480px)',
                  },
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: mediumSmallImageUrl,
                    media: '(max-width: 768px)',
                  },
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: mediumImageUrl,
                    media: '(max-width: 944px)',
                  },
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: largeImageUrl,
                    media: '(max-width: 1200px)',
                  },
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: xLargeImageUrl,
                    media: '(min-width: 1440px)',
                  },
                ]}
              />
            : <div className="hero-image__image" />
        }
      </div>
    )
  }

  renderOverlay () {
    const { props } = this
    const { hasOverlay, overlayOpacity, overlayClassName } = props
    if (hasOverlay) {
      const base = 'hero-image__overlay'
      const className = [base].concat(overlayClassName || [])
      // _isArray(overlayClassName) ? overlayClassName : [],
      if (overlayOpacity === HERO_IMAGE_OVERLAY_OPACITY_SUPER_LIGHT) {
        className.push(`${base}--super-light`)
      } else if (overlayOpacity === HERO_IMAGE_OVERLAY_OPACITY_LIGHT) {
        className.push(`${base}--light`)
      } else if (overlayOpacity === HERO_IMAGE_OVERLAY_OPACITY_MEDIUM) {
        className.push(`${base}--medium`)
      } else if (overlayOpacity === HERO_IMAGE_OVERLAY_OPACITY_DARK) {
        className.push(`${base}--dark`)
      } else if (overlayOpacity === HERO_IMAGE_OVERLAY_LEFT_TO_RIGHY_OPACITY) {
        className.push(`${base}--left-to-right`)
      } else if (overlayOpacity === HERO_IMAGE_OVERLAY_TO_RIGHY_OPACITY_DARK) {
        className.push(`${base}--to-right-dark`)
      }
      return <div className={className.join(' ')} />
    }
    return null
  }

  render () {
    const { props } = this
    const { className, optimizeImage } = props

    return (
      <div className={getClassName(className)}>
        {this.renderOverlay()}
        {optimizedImagesEnabled && optimizeImage ? this.renderOptimizedImage() : this.renderImage()}
      </div>
    )
  }
}

HeroImage.props = {
  className: PropTypes.array,
  overlayClassName: PropTypes.array,
  url: PropTypes.string,
  smallUrl: PropTypes.string,
  mediumSmallUrl: PropTypes.string,
  mediumUrl: PropTypes.string,
  largeUrl: PropTypes.string,
  hasOverlay: PropTypes.bool,
  alwaysUseProps: PropTypes.bool,
  overlayOpacity: PropTypes.string,
  viewportWidth: PropTypes.number.isRequired,
  optimizeImage: PropTypes.bool,
}

HeroImage.defaultProps = {
  optimizeImage: true,
}

export default compose(
  connectRedux(
    (state) => {
      return {
        viewportWidth: state.app.getIn(['viewport', 'width']),
      }
    },
  ),
)(HeroImage)
